import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentConversation } from '../../../reducers/aiAssistant.selectors';
import { getAIAssistantPageSource } from '../../../utils/getAIAssistantPageSource';
import useRouter from '../../../utils/useRouter';
import services from '../../../services';
import { PodcastMessageContext } from './podcastTypes';

// Shared by the detail view player and the extended chat card so both emit identical events.
const usePodcastAudioActionTracking = (podcastId: string | undefined) => {
  const currentConversation = useSelector(getCurrentConversation);
  const { match: { location: { pathname } } } = useRouter();

  const trackAudioActionPerformed = useCallback(
    (type: 'play audio' | 'pause audio') => {
      const conversationId = currentConversation?.data?.id;
      const messages = currentConversation?.data?.messages;
      if (!conversationId || !messages || !podcastId) return;

      const requestIndex = messages.findIndex((msg) => (
        msg.role === 'assistant'
        && (msg.context as PodcastMessageContext | undefined)?.generatedPodcastId === podcastId
      ));
      const requestMessage = requestIndex === -1 ? null : messages[requestIndex];
      if (!requestMessage) return;

      // The user prompt that requested the podcast is the last user message before the response.
      const userMessage = messages
        .slice(0, requestIndex)
        .reverse()
        .find((message) => message.role === 'user') ?? null;

      services.track.ampliTrackAIAssistantActionPerformedEvent({
        conversationId,
        requestId: requestMessage.id,
        pageSource: getAIAssistantPageSource(pathname),
        feature: ['podcast generation'],
        type,
        userPrompt: userMessage?.content[0]?.text,
      });
    },
    [currentConversation, podcastId, pathname]
  );

  const handleAudioPlayTrack = useCallback(
    () => trackAudioActionPerformed('play audio'),
    [trackAudioActionPerformed]
  );

  const handleAudioPauseTrack = useCallback(
    () => trackAudioActionPerformed('pause audio'),
    [trackAudioActionPerformed]
  );

  return { handleAudioPlayTrack, handleAudioPauseTrack };
};

export default usePodcastAudioActionTracking;
